import axios from 'axios'
import { ElMessage } from 'element-plus'
import {
  BASE_URL,
  REQUEST_TIMEOUT,
  HTTP_STATUS,
  BUSINESS_CODE,
  STORAGE_KEY,
  RETRY_CONFIG
} from './constants'
import authStorage from '@/utils/authStorage';
import { platform } from '@/platform';

const instance = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

let isRefreshing = false
let failedQueue = []

let isLoggingOut = false

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const getRefreshToken = () => localStorage.getItem(STORAGE_KEY.REFRESH_TOKEN)

const clearToken = () => {
  authStorage.removeToken()
  localStorage.removeItem(STORAGE_KEY.REFRESH_TOKEN)
  localStorage.removeItem("vuex")
  localStorage.removeItem('user_info')
}

const refreshAccessToken = async () => {
  try {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await axios.post(`${BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken
    })

    const { access_token, refresh_token } = response.data.data
    authStorage.setToken(access_token)

    if (refresh_token) {
      localStorage.setItem(STORAGE_KEY.REFRESH_TOKEN, refresh_token)
    }

    return access_token
  } catch (error) {
    clearToken()
    window.location.href = '/login'
    throw error
  }
}

instance.interceptors.request.use(
  config => {
    const token = authStorage.getToken()
    if (token && !config.headers.skipToken) {
      config.headers.Authorization = `Bearer ${token}`
    }

    if (config.method?.toLowerCase() === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    return config
  },
  error => Promise.reject(error)
)

instance.interceptors.response.use(
  response => response.data,
  async error => {
    const originalRequest = error.config

    if (!error.response) {
      error.message = '网络连接失败，请检查网络设置'
      return Promise.reject(error)
    }

    const { status, data } = error.response
    const businessCode = data?.code

    if (error.code === 'ECONNABORTED' || status === HTTP_STATUS.GATEWAY_TIMEOUT) {
      error.message = '请求超时，请稍后重试'
      return Promise.reject(error)
    }

    if (status === HTTP_STATUS.UNAUTHORIZED) {//暂时没有用到t
      if (businessCode === BUSINESS_CODE.TOKEN_EXPIRED && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return instance(originalRequest)
          }).catch(err => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const newToken = await refreshAccessToken()
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          processQueue(null, newToken)
          return instance(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          clearToken()
          window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname)
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      clearToken()
      //window.location.href = '/login'
      return Promise.reject(error.response.data.msg || status)
    }

    if (status === HTTP_STATUS.FORBIDDEN) {
      error.message = '权限不足，无法访问该资源'
      //window.location.href = '/404'
      return Promise.reject(error)
    }

    if (status >= 500 && RETRY_CONFIG.RETRY_STATUS_CODES.includes(status)) {
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0
      }

      if (originalRequest._retryCount < RETRY_CONFIG.MAX_RETRIES) {
        originalRequest._retryCount++

        await new Promise(resolve =>
          setTimeout(resolve, RETRY_CONFIG.RETRY_DELAY * originalRequest._retryCount)
        )

        return instance(originalRequest)
      }
    } else if (status == 424) {
      if (!isLoggingOut) {
        isLoggingOut = true
        clearToken()
        platform.handleLogout()
        ElMessage({
          type: 'info',
          message: '登录已过期，请重新登录',
          duration: 2000,
          grouping: true
        })
        //alert(JSON.stringify(error))
        //window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname)
      }
      return Promise.reject(error.response.data.msg || status)
    }

    if (status == HTTP_STATUS.BAD_REQUEST) {
      return Promise.reject(data?.msg || '服务器错误，请稍后重试')
    }

    error.message = data?.message || '服务器错误，请稍后重试'
    return Promise.reject(error)
  }
)

// 导出实例和常用方法
export default instance

// 常用请求方法的快捷导出
export const get = (url, params = {}, config = {}) =>
  instance.get(url, { params, ...config })

export const post = (url, data = {}, config = {}) =>
  instance.post(url, data, config)

export const put = (url, data = {}, config = {}) =>
  instance.put(url, data, config)

export const del = (url, data = {}, config = {}) =>
  instance.delete(url, { data, ...config })

export const upload = (url, file, onProgress = null) => {
  const formData = new FormData()
  formData.append('file', file)

  return instance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
  })
}